import { inject, injectable } from 'inversify';

import { LoginServiceConstants } from '../commons/commons';
import { Logger } from '../models/logger';
import { hasImpersonationRecord, readImpersonation, tokenImpersonationSessionId } from '../utils/impersonation-storage';
import { LightHttp } from './light-http.service';
import { StorageKeyPrefixeTypes, StorageService } from './storage.service';

@injectable()
export class AuthService {
    private readonly logger = Logger.getLogger('AuthService');

    authToken: string;

    constructor(
        @inject(StorageService) private storageService: StorageService,
    ) {
        LightHttp.registerAuthTokenProvider(this);
    }

    /**
     * Admin v2 impersonation (API-CONTRACT §5): while this tab holds an `AUTH_IMP` record its token wins -- even once
     * expired (LightHttp then refuses the request and ends the session; an expired record never falls back to the
     * admin's bearer). Otherwise the browser's own bearer, as before.
     */
    public getToken(): string {
        const imp = readImpersonation();
        if (imp) return imp.token;
        return this.authToken || this.storageService.get<string>(StorageKeyPrefixeTypes.USER_SETTING_KEY_PREFIX, LoginServiceConstants.AUTH_BEARER_STORAGE_KEY);
    }

    /**
     * Setting it to undefined or null is considered as no valid token.
     *
     * Never while impersonating (the admin's `localStorage` AUTH, shared by every tab, is not this tab's to change),
     * and never an impersonation token (`imp` claim) -- that one lives in `sessionStorage` only.
     * @param token
     */
    public setAuthToken(token: string) {
        if (hasImpersonationRecord()) {
            this.logger.warn({ message: 'setAuthToken ignored: this tab is viewing as a user' });
            return;
        }
        if (tokenImpersonationSessionId(token)) {
            this.logger.warn({ message: 'setAuthToken refused an impersonation token' });
            return;
        }
        this.storageService.store(StorageKeyPrefixeTypes.USER_SETTING_KEY_PREFIX, LoginServiceConstants.AUTH_BEARER_STORAGE_KEY, token);
        this.authToken = token;
    }

    /** No-op while impersonating: ending the impersonation is `ImpersonationService.end`, never the admin's logout. */
    public unsetAuthToken() {
        if (hasImpersonationRecord()) {
            this.logger.warn({ message: 'unsetAuthToken ignored: this tab is viewing as a user' });
            return;
        }
        this.storageService.remove(StorageKeyPrefixeTypes.USER_SETTING_KEY_PREFIX, LoginServiceConstants.AUTH_BEARER_STORAGE_KEY);
        this.authToken = null;
    }
}
